import { getAmplitudeInstance } from '../amplitudeHelpers';
import FilterApplicationMethod from '../enums/FilterApplicationMethod';
import FilterSheetType from '../enums/FilterSheetType';
import SearchDifficultyFilter from '../enums/SearchDifficultyFilter';
import SearchOrigin from '../enums/SearchOrigin';
import SearchRouteTypeFilter from '../enums/SearchRouteTypeFilter';
import SearchSortFilter from '../enums/SearchSortFilter';
import SearchTrailCompletedFilter from '../enums/SearchTrailCompletedFilter';
import SearchTrailTrafficFilter from '../enums/SearchTrailTrafficFilter';

type logExploreFiltersAppliedParams = {
  activity_type_filters?: string;
  attractions_filters?: string[];
  difficulty_filters?: SearchDifficultyFilter[];
  distance_away_filter?: number;
  elevation_filters?: string;
  filter_application_method?: FilterApplicationMethod[];
  filter_id?: number;
  filter_sheet_type?: FilterSheetType;
  highest_point_filter?: string;
  is_metric_user?: boolean;
  is_saved_filter_set?: boolean;
  length_filters?: string;
  limit_filters?: number;
  location_filters?: string;
  num_filter_items_selected?: number;
  num_filters_selected: number;
  num_results_returned: number;
  rating_filters?: number;
  route_type_filters?: SearchRouteTypeFilter[];
  search_origin: SearchOrigin;
  sort_filters?: SearchSortFilter;
  suitability_filters?: string[];
  trail_completion_filters?: SearchTrailCompletedFilter[];
  trail_traffic_filters?: SearchTrailTrafficFilter[];
};

/**
 * User has finished applying all of their filter(s) and is shown search results (event that occurs after 'See Trails' is clicked). This event should be triggered by the bottom sheet for individual filters introduced in Phase 3 work.
 * @param params - Attributes to log with the amplitude event
 * @param params.activity_type_filters - A JSON encoded array of the activity type filter server uids applied by the user. Note: with filters updates in 02-14 this will become a single value for all searches.
 * @param params.attractions_filters - A JSON encoded array of the attractions filter server uids applied by the user.
 * @param params.difficulty_filters - A JSON encoded array of the difficulty filters applied by the user.
 * @param params.distance_away_filter - An integer denoting the distance away filter (in miles) applied by the user.
 * @param params.elevation_filters - A JSON encoded object of the min & max elevation gain filters (feet) applied in the format {'min':int, 'max':int}
 * @param params.filter_application_method - How users applied filters in the filter sheet
 * @param params.filter_id - The unique identifier for the filter set (if applicable).
 * @param params.filter_sheet_type - Filter sheet type (full sheet or mini sheet)
 * @param params.highest_point_filter - A JSON encoded object of the min & max highest point filter (feet) applied in the format {'min':int, 'max':int}
 * @param params.is_metric_user - A boolean denoting if the user has metric values displayed on their filters. This WILL NOT affect the values passed for other filters (miles/feet), but informs how users see the product.
 * @param params.is_saved_filter_set - Boolean denoting if the filters were applied from a saved filter set.
 * @param params.length_filters - A JSON encoded object of the min & max length filters (miles) applied in the format {'min':int, 'max':int}
 * @param params.limit_filters - A filter on the number of results returned
 * @param params.location_filters - A JSON encoded object of the location applied in the format {'lat':float, 'lng':float}. Map view: northeast corner of the map when filters were applied. Homepage/text: send lat/lng of search location..
 * @param params.num_filter_items_selected - The total number of filter items, regardless of what grouping applied (e.g. hard, medium, hiking, mountain biking, if selected should be a count of 4.).
 * @param params.num_filters_selected - The total number of filter groups applied (e.g. activity type, difficulty if selected should be a count of 3.).
 * @param params.num_results_returned - The number of results returned to the user
 * @param params.rating_filters - Integer denoting the star rating of a filter applied.
 * @param params.route_type_filters - A JSON encoded array of the route type filters applied by the user. O - Out and back, L - Loop, P - Point to point
 * @param params.search_origin - The in-app location where search was accessed
 * @param params.sort_filters - The sort option selected by the user
 * @param params.suitability_filters - A JSON encoded array of the suitability filter server uids applied by the user.
 * @param params.trail_completion_filters - A JSON encoded array of the trail completion filters applied by the user.
 * @param params.trail_traffic_filters - A JSON encoded array of the trail traffic filters applied by the user.
 */
const logExploreFiltersApplied = (
  {
    activity_type_filters,
    attractions_filters,
    difficulty_filters,
    distance_away_filter,
    elevation_filters,
    filter_application_method,
    filter_id,
    filter_sheet_type,
    highest_point_filter,
    is_metric_user,
    is_saved_filter_set,
    length_filters,
    limit_filters,
    location_filters,
    num_filter_items_selected,
    num_filters_selected,
    num_results_returned,
    rating_filters,
    route_type_filters,
    search_origin,
    sort_filters,
    suitability_filters,
    trail_completion_filters,
    trail_traffic_filters
  }: logExploreFiltersAppliedParams,
  callback?: () => void
) => {
  getAmplitudeInstance().logEvent(
    'explore filters applied',
    {
      'activity type filters': activity_type_filters,
      'attractions filters': attractions_filters,
      'difficulty filters': difficulty_filters,
      'distance away filter': distance_away_filter,
      'elevation filters': elevation_filters,
      'filter application method': filter_application_method,
      'filter id': filter_id,
      'filter sheet type': filter_sheet_type,
      'highest point filter': highest_point_filter,
      'is metric user': is_metric_user,
      'is saved filter set': is_saved_filter_set,
      'length filters': length_filters,
      'limit filters': limit_filters,
      'location filters': location_filters,
      'num filter items selected': num_filter_items_selected,
      'num filters selected': num_filters_selected,
      'num results returned': num_results_returned,
      'rating filters': rating_filters,
      'route type filters': route_type_filters,
      'search origin': search_origin,
      'sort filters': sort_filters,
      'suitability filters': suitability_filters,
      'trail completion filters': trail_completion_filters,
      'trail traffic filters': trail_traffic_filters
    },
    callback
  );
};

export default logExploreFiltersApplied;
